import InfoRounded from '@mui/icons-material/InfoRounded';
import Grid from '@mui/material/Grid';
import { AlertTooltip } from '@openx/components-core/lib/AlertTooltip/AlertTooltip';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete';
import { ListTypesAPI } from '@openx/types/inventoryList';
import { AppInventoryVersion, type AppInventoryVersionType } from '@openx/types/targeting/appInventory';
import {
  type AppBundleIdOptionPayload,
  type AppInventoryOptionPayload,
  AppInventoryType,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import type { Dispatch, JSX, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnyAction } from 'redux';
import { useTargetingContext } from '../../../../utils/context/TargetingContext';
import { useTargetingDisabledState } from '../../../../utils/hooks/useTargetingDisabledState';
import { useTargetingOptionDisabledState } from '../../../../utils/hooks/useTargetingOptionDisabledState';
import type { InvalidItemsError } from '../../../shared/BulkAdd/types';
import { INVENTORY_LIMITS } from '../../../shared/InventoryAndContent/constants';
import { useAppInventoryInventoryListAllowed } from '../../../shared/InventoryAndContent/featureFlags';
import { setComparisonOpValue } from '../../../shared/InventoryAndContent/inventoryHelpers';
import { AppInventoryInput } from '../AppInventoryInput/AppInventoryInput';
import { appInventoryTypeOptions, type SetAppInventoryTypeDispatch } from '../constants';
import { getItemsCount } from '../utils/getItemsCount';
import { handleDuplicateErrorHandling } from '../utils/handleDuplicateErrorHandling';

type AppInventoryComparisonProps = {
  appInventoryType?: AppInventoryType;
  setAppInventoryType?: SetAppInventoryTypeDispatch;
  appNameComparisonType?: ComparisonType;
  bundleIdComparisonType?: ComparisonType;
  dispatchInventory: (action: AnyAction) => void;
  appInventoryContent: AppInventoryOptionPayload;
  appBundleIdContent: AppBundleIdOptionPayload;
  accountUid?: string;
  setAppBundleIdDuplicateErrors?: Dispatch<SetStateAction<InvalidItemsError | null>>;
  inventoryVersion?: AppInventoryVersionType;
};

// App Inventory TopBox content component with dropdown for type selection and input for adding values.
export const AppInventoryComparison = ({
  appInventoryType: appInventoryTypeProp,
  setAppInventoryType: setAppInventoryTypeProp,
  appNameComparisonType: appNameComparisonTypeProp,
  bundleIdComparisonType: bundleIdComparisonTypeProp,
  dispatchInventory,
  appInventoryContent,
  appBundleIdContent,
  accountUid = '',
  setAppBundleIdDuplicateErrors,
  inventoryVersion,
}: AppInventoryComparisonProps): JSX.Element => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  // Local state fallbacks (used when props are not provided)
  const [localAppInventoryType, setLocalAppInventoryType] = useState<AppInventoryType>(AppInventoryType.APP_NAME);

  const appInventoryType = appInventoryTypeProp ?? localAppInventoryType;
  const setAppInventoryType = setAppInventoryTypeProp ?? setLocalAppInventoryType;
  const appNameComparisonType = appNameComparisonTypeProp ?? ComparisonType.EQUALS;
  const bundleIdComparisonType = bundleIdComparisonTypeProp ?? ComparisonType.EQUALS;

  const isInventoryListAllowed = useAppInventoryInventoryListAllowed();

  const { getOptionFieldPath } = useTargetingOptionDisabledState();

  const { isDisabled: isAppBundleIdDisabled, disabledReasonMessage: appBundleIdDisabledReasonMessage } =
    useTargetingDisabledState(getOptionFieldPath(InventoryContentOption.APP_BUNDLE_ID));

  const { isDisabled: isAppGroupDisabled, disabledReasonMessage: appGroupDisabledReasonMessage } =
    useTargetingDisabledState(getOptionFieldPath(InventoryContentOption.APP_GROUP));

  // Inventory List data fetching
  const { useInventoryListsOptionsFetch, useInventoryListByIdFetch } = useTargetingContext();
  const {
    inventoryListsOptions,
    loading: loadingLists,
    mapById,
  } = useInventoryListsOptionsFetch(ListTypesAPI.BUNDLE, accountUid);
  const { inventoryList, loading: loadingInventoryList } = useInventoryListByIdFetch(
    appBundleIdContent.inventorylist_id
  );

  // Get current comparison type based on selected app inventory type
  const comparisonType = useMemo(
    () => (appInventoryType === AppInventoryType.APP_NAME ? appNameComparisonType : bundleIdComparisonType),
    [appInventoryType, appNameComparisonType, bundleIdComparisonType]
  );

  // Get content option based on selected type
  const contentOption = useMemo(
    () =>
      appInventoryType === AppInventoryType.APP_NAME
        ? InventoryContentOption.APP_GROUP
        : InventoryContentOption.APP_BUNDLE_ID,
    [appInventoryType]
  );

  // Get current content based on selected type
  const currentContent = useMemo(
    () => (appInventoryType === AppInventoryType.APP_NAME ? appInventoryContent : appBundleIdContent),
    [appInventoryType, appInventoryContent, appBundleIdContent]
  );

  // Current values for the current comparison type
  const currentValues = useMemo(
    () => currentContent?.val?.[comparisonType] ?? [],
    [currentContent?.val, comparisonType]
  );

  // App Bundle ID limit handling
  const { isAppBundleLimitReached, appBundleIdErrorText } = useMemo(() => {
    const appBundleIdLimit = INVENTORY_LIMITS[AppInventoryType.APP_BUNDLE_ID];
    const appBundleIdErrorText = t('The limit has been reached ({limit}).', { limit: appBundleIdLimit });

    return {
      isAppBundleLimitReached: currentValues.length >= appBundleIdLimit,
      appBundleIdErrorText,
    };
  }, [currentValues, t]);

  // All values to check for duplicates (both EQUALS and DOES_NOT_EQUAL)
  const allValues = useMemo(() => {
    const equals = currentContent?.val?.[ComparisonType.EQUALS] ?? [];
    const notEquals = currentContent?.val?.[ComparisonType.DOES_NOT_EQUAL] ?? [];

    return [...equals, ...notEquals];
  }, [currentContent?.val]);

  const itemsCount = useMemo(() => getItemsCount(currentContent?.val ?? {}), [currentContent?.val]);
  const isLimitReached = itemsCount >= INVENTORY_LIMITS[contentOption];

  // Labels for App Inventory Type dropdown
  const appInventoryTypeLabels = useMemo(
    () => ({
      [AppInventoryType.APP_NAME]: t('App Name'),
      [AppInventoryType.APP_BUNDLE_ID]: t('App Bundle ID - Manually'),
      [AppInventoryType.INVENTORY_LIST]: t('App Bundle ID - From the Inventory List (Allow Only)'),
    }),
    [t]
  );

  const isInventoryList = appInventoryType === AppInventoryType.INVENTORY_LIST;
  // Inventory Lists are Bundle ID only; App Name blocking is handled in AppInventoryItemsSection
  const isBlockMode =
    appNameComparisonType === ComparisonType.DOES_NOT_EQUAL || bundleIdComparisonType === ComparisonType.DOES_NOT_EQUAL;
  const isInventoryV2nly = inventoryVersion === AppInventoryVersion.APP_INVENTORY_ONLY_V2;

  const availableAppInventoryTypeOptions = useMemo(
    () =>
      isInventoryListAllowed
        ? appInventoryTypeOptions
        : appInventoryTypeOptions.filter(option => option !== AppInventoryType.INVENTORY_LIST),
    [isInventoryListAllowed]
  );

  const disabledReasonMessage =
    (appInventoryType === AppInventoryType.APP_NAME && appGroupDisabledReasonMessage) ||
    ((appInventoryType === AppInventoryType.APP_BUNDLE_ID || appInventoryType === AppInventoryType.INVENTORY_LIST) &&
      appBundleIdDisabledReasonMessage) ||
    undefined;

  // Handle manual input for Bundle ID (Enter key)
  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (appInventoryType !== AppInventoryType.APP_BUNDLE_ID || isAppBundleIdDisabled || isLimitReached) return;

      const targetElement = e.target as HTMLInputElement;
      const { value } = targetElement;

      if (e.key === 'Enter' && value.trim()) {
        const trimmedValue = value.trim();

        if (allValues.includes(trimmedValue) || isAppBundleLimitReached) {
          return;
        }

        const newValues = [...currentValues, trimmedValue];

        dispatchInventory(
          setComparisonOpValue[contentOption]({
            values: newValues,
            comparisonType,
          })
        );

        targetElement.value = '';
        setInputValue('');
      }
    },
    [
      appInventoryType,
      isAppBundleIdDisabled,
      isLimitReached,
      allValues,
      currentValues,
      comparisonType,
      contentOption,
      dispatchInventory,
      isAppBundleLimitReached,
    ]
  );

  // Handle Inventory List selection
  const handleInventoryListChange = useCallback(
    (_: unknown, value: string | null) => {
      if (isAppBundleIdDisabled) return;

      dispatchInventory(
        setComparisonOpValue[InventoryContentOption.APP_BUNDLE_ID]({
          comparisonType: bundleIdComparisonType,
          inventorylist_id: value ?? undefined,
        })
      );
    },
    [dispatchInventory, bundleIdComparisonType, isAppBundleIdDisabled]
  );

  const getInventoryListOptionLabel = useCallback(
    (option: string): string => {
      if (mapById[option]?.name) return mapById[option].name;
      // During loading, show the ID as fallback to avoid label flickering
      if (loadingLists && appBundleIdContent.inventorylist_id) return option;

      return '';
    },
    [mapById, loadingLists, appBundleIdContent.inventorylist_id]
  );

  // Real-time duplicate detection: runs whenever inventory list data or manual bundle IDs change,
  // providing immediate feedback rather than deferring to submit/validate.
  useEffect(() => {
    if (
      !setAppBundleIdDuplicateErrors ||
      !appBundleIdContent.inventorylist_id ||
      loadingInventoryList ||
      inventoryList?.uid !== appBundleIdContent.inventorylist_id
    ) {
      return;
    }

    handleDuplicateErrorHandling({
      inventoryListValues: inventoryList?.values || [],
      inventoryContent: appBundleIdContent,
      setAppBundleIdDuplicateErrors,
      dispatchInventory,
    });
  }, [loadingInventoryList, inventoryList, appBundleIdContent, setAppBundleIdDuplicateErrors, dispatchInventory]);

  return (
    <>
      <Grid size={isInventoryV2nly ? 4 : 'grow'}>
        <AutoComplete
          options={[...availableAppInventoryTypeOptions]}
          getOptionLabel={(option: AppInventoryType) => appInventoryTypeLabels[option]}
          getOptionDisabled={option =>
            (option === AppInventoryType.INVENTORY_LIST && (isBlockMode || isAppBundleIdDisabled)) ||
            (option === AppInventoryType.APP_BUNDLE_ID && isAppBundleIdDisabled) ||
            (option === AppInventoryType.APP_NAME && isAppGroupDisabled)
          }
          textFieldReadOnly
          disableClearable
          onChange={(_e, value) => setAppInventoryType(value)}
          value={appInventoryType}
          data-test="app-inventory-type-dropdown"
          textFieldProps={{
            label: t('App Inventory'),
          }}
          renderOptions={{
            dataTest: 'app-inventory-type-option',
          }}
        />
      </Grid>

      <Grid size="auto" sx={{ mt: 2 }} data-test="app-inventory-list-info-tooltip">
        <AlertTooltip
          icon={<InfoRounded />}
          color="default"
          title={
            <>
              <div>{t("App Bundle ID - From Inventory List is restricted to 'Allow' targeting only.")}</div>
              <br />
              <div>{t("Combining them requires App Name and Manual IDs to be set to 'Allow'.")}</div>
              {disabledReasonMessage && (
                <>
                  <br />
                  <div>{disabledReasonMessage}</div>
                </>
              )}
            </>
          }
        />
      </Grid>

      <Grid size={isInventoryV2nly ? 'grow' : 12}>
        {appInventoryType === AppInventoryType.APP_NAME && (
          <AppInventoryInput
            comparisonType={comparisonType}
            dispatchInventory={dispatchInventory}
            currentValues={allValues}
            appInventoryType={AppInventoryType.APP_NAME}
            placeholder={t('Provide or search for App Name')}
            testId="app-inventory-input"
            isDisabled={isAppGroupDisabled}
            isLimitReached={isLimitReached}
            limit={INVENTORY_LIMITS[contentOption]}
          />
        )}

        {appInventoryType === AppInventoryType.APP_BUNDLE_ID && (
          <AutoComplete<string, false, false, true>
            options={[]}
            inputValue={inputValue}
            onInputChange={(_e, value) => {
              if (isAppBundleIdDisabled || isLimitReached) return;

              setInputValue(value);
            }}
            onKeyUp={handleKeyUp}
            textFieldProps={{
              label: t('App Bundle ID'),
              placeholder: t('Provide App Bundle ID and press Enter'),
              error: isAppBundleLimitReached,
              helperText: isAppBundleLimitReached ? appBundleIdErrorText : null,
            }}
            data-test="app-bundle-id-input"
            freeSolo
            value={null}
            disabled={isAppBundleIdDisabled || isLimitReached}
          />
        )}

        {isInventoryList && (
          <AutoComplete
            textFieldProps={{
              label: t('Inventory List'),
              placeholder: t('Select or Search an Inventory List'),
            }}
            options={inventoryListsOptions}
            getOptionLabel={getInventoryListOptionLabel}
            onChange={handleInventoryListChange}
            value={appBundleIdContent.inventorylist_id || null}
            disabled={loadingLists || isBlockMode || isAppBundleIdDisabled}
            loading={loadingLists}
            data-test="app-inventory-list-autocomplete"
          />
        )}
      </Grid>
    </>
  );
};
